import type { BlockId, SerializedNodeLike } from "./types";

/**
 * Where Lexical serializes NodeState. Repeated here because core cannot import the runtime; a unit
 * test holds it equal to Lexical's `NODE_STATE_KEY`.
 */
export const NODE_STATE_KEY = "$";
export const BLOCK_ID_KEY = "blockId";

/**
 * Serialized node types that carry a persistent ID: every block the editor can put at the root,
 * plus list items. A table has one, as the unit that is selected and moved; its rows and cells do
 * not (nothing addresses them), and the paragraphs in its cells do, as paragraphs. An attachment
 * has one, as the block it is.
 */
export const IDENTIFIED_NODE_TYPES: ReadonlySet<string> = new Set([
  "paragraph",
  "heading",
  "quote",
  "code",
  "list",
  "listitem",
  "horizontalrule",
  "table",
  "attachment",
]);

export const BLOCK_ID_LENGTH = 12;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const BLOCK_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export type BlockIdGenerator = () => BlockId;

/** URL-safe, so an ID can be a fragment or an attribute value as it is. Hosts may supply their own. */
export const isBlockId = (value: unknown): value is BlockId =>
  typeof value === "string" && BLOCK_ID_PATTERN.test(value);

/** A generator over any source of numbers in [0, 1); seed it for reproducible tests and fixtures. */
export function createBlockIdGenerator(random: () => number): BlockIdGenerator {
  return () => {
    let id = "";
    for (let index = 0; index < BLOCK_ID_LENGTH; index++) {
      id += ALPHABET[Math.floor(random() * ALPHABET.length) % ALPHABET.length];
    }
    return id;
  };
}

/** 72 bits from the platform's CSPRNG. 256 is a multiple of 64, so masking is unbiased. */
export const generateBlockId: BlockIdGenerator = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(BLOCK_ID_LENGTH));
  let id = "";
  for (const byte of bytes) id += ALPHABET[byte & 63];
  return id;
};

/**
 * The same seed always yields the same IDs. Used where content is identified without a durable
 * write yet (a migration, a repair on open), so every reader of the same stored bytes agrees on
 * the IDs until one of them saves.
 */
export function createSeededBlockIdGenerator(seed: string): BlockIdGenerator {
  // FNV-1a into a mulberry32 state.
  let state = 0x811c9dc5;
  for (let index = 0; index < seed.length; index++) {
    state = Math.imul(state ^ seed.charCodeAt(index), 0x01000193);
  }
  return createBlockIdGenerator(() => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  });
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The node's block ID, or `undefined` when it has none or it is not a well-formed one. */
export function readBlockId(node: SerializedNodeLike): BlockId | undefined {
  const state = node[NODE_STATE_KEY];
  if (!isRecord(state)) return undefined;
  const id = state[BLOCK_ID_KEY];
  return isBlockId(id) ? id : undefined;
}

const needsBlockId = (node: Record<string, unknown>): boolean => {
  if (typeof node.type !== "string" || !IDENTIFIED_NODE_TYPES.has(node.type)) return false;
  const state = node[NODE_STATE_KEY];
  // Malformed state is left for validation to report rather than papered over here.
  if (state !== undefined && !isRecord(state)) return false;
  return state === undefined || state[BLOCK_ID_KEY] === undefined;
};

export interface AssignBlockIdsResult {
  root: SerializedNodeLike;
  /** How many nodes were given an ID. Zero means `root` is the input, untouched. */
  assigned: number;
}

/**
 * Gives every identified node that lacks an ID one, in document order, without mutating the input
 * and without touching IDs that are already there. Runs on unvalidated JSON, so it is iterative
 * (hostile nesting must reach the depth check, not overflow the stack) and tolerant of junk.
 */
export function assignBlockIds(
  root: SerializedNodeLike,
  generate: BlockIdGenerator,
): AssignBlockIdsResult {
  const taken = new Set<string>();
  let missing = 0;
  const scan: unknown[] = [root];
  while (scan.length > 0) {
    const node = scan.pop();
    if (!isRecord(node)) continue;
    if (needsBlockId(node)) missing++;
    const id = readBlockId(node as SerializedNodeLike);
    if (id !== undefined) taken.add(id);
    if (Array.isArray(node.children)) for (const child of node.children) scan.push(child);
  }
  if (missing === 0) return { root, assigned: 0 };

  const next = (): BlockId => {
    let id = generate();
    while (taken.has(id)) id = generate();
    taken.add(id);
    return id;
  };
  const copy: Record<string, unknown> = { ...root };
  const stack: Record<string, unknown>[] = [copy];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (needsBlockId(node)) {
      const state = node[NODE_STATE_KEY];
      node[NODE_STATE_KEY] = { ...(isRecord(state) ? state : {}), [BLOCK_ID_KEY]: next() };
    }
    if (Array.isArray(node.children)) {
      const children: unknown[] = node.children.map((child: unknown) =>
        isRecord(child) ? { ...child } : child,
      );
      node.children = children;
      // Reversed, so nodes are popped (and identified) in document order.
      for (let index = children.length - 1; index >= 0; index--) {
        const child = children[index];
        if (isRecord(child)) stack.push(child);
      }
    }
  }
  return { root: copy as SerializedNodeLike, assigned: missing };
}
